import React, { useMemo, memo } from 'react';
import { pdf } from '@react-pdf/renderer';

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import Icons from '../../common/Icons';

// MODALES Y COMPONENTES RECONECTADOS DEL PROYECTO
import ModalEdicionContrato from '../../../../ui/ModalEdicionContrato';
import ModalNotificacion from '../../../../ui/ModalNotificacion'; 
import PlantillaCotizacionArtPDF from '../PlantillaPdf/PlantillaCotizacionArtPDF';

// IMPORTAMOS EL FORMULARIO DE CLIENTE PARA COMPLETAR EL ALTA PREMIUM
import FormularioCliente from '../../../../Formularios/FormularioCliente'; // ◄ RUTA DE IMPORTACIÓN UNIFICADA

// CONTROLADOR DE NEGOCIO MODULAR Y PERMISOS CENTRALIZADOS
import { useDetalleCotizacionArt } from './useDetalleCotizacionArt';
import { usePermissions } from '../../../../hooks/usePermissions'; // Hook de permisos unificado de la matriz ACL

// --- HELPERS DE FORMATO INTERNOS ---
const formatCurrency = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val || 0);
const formatPercent = (val) => `${Number(val || 0).toFixed(2).replace('.', ',')}%`;

// --- DETALLE PRESENTACIONAL ---
const DetalleCotizacionArt = ({ cotizacion: initialCotizacion, onBack }) => {
    
    // Consumimos el core analítico del hook modular
    const {
        loading,
        cotizacion,
        fetchedClient,
        sourceData,
        isProspect,
        isContratoGenerado,
        ofertas,
        listaVisual,
        ofertaParaCalculos,
        selectedComparacion,
        setSelectedComparacion,
        showContratoModal,
        setShowContratoModal,
        showConfirmModal,
        setShowConfirmModal,
        notification,
        setNotification,
        contratoPrefill,
        clientDataForModal,
        isGenerating,
        setIsGenerating,
        prospectInitialData, // ◄ Recibimos la hidratación del prospecto
        handleConversionSuccess, // ◄ Recibimos la acción atómica de guardado
        handleContractSuccess,
        handleOpenContractModal
    } = useDetalleCotizacionArt(initialCotizacion, onBack);

    // Consumimos los permisos reales de nuestra matriz ACL centralizada en memoria (Cero Flicker)
    const { isFullUser, loadingAuth } = usePermissions();

    // --- CÁLCULOS ESTADÍSTICOS MEMOIZADOS EN LA VISTA ---
    const fullName = useMemo(() => {
        if (!sourceData) return 'Cargando...';
        const nom = sourceData.clienteNombre || sourceData.nombre || sourceData.entidadNombre || '';
        const ape = sourceData.clienteApellido || sourceData.apellido || '';
        const res = `${nom} ${ape}`.trim();
        return res === '' ? 'SIN NOMBRE' : res.toUpperCase();
    }, [sourceData]);

    const displayEmail = useMemo(() => {
        if (Array.isArray(sourceData?.emails) && sourceData.emails.length > 0) {
            const first = sourceData.emails[0];
            return (typeof first === 'object' ? (first.valor || first.email) : first) || 'No informado';
        }
        return sourceData?.email || sourceData?.clienteEmail || sourceData?.entidadEmail || 'No informado';
    }, [sourceData]);

    const displayPhone = useMemo(() => {
        if (Array.isArray(sourceData?.telefonos) && sourceData.telefonos.length > 0) {
            const first = sourceData.telefonos[0];
            return (typeof first === 'object' ? (first.valor || first.telefono) : first) || 'No informado';
        }
        return sourceData?.telefono || sourceData?.clienteTelefono || sourceData?.entidadPhone || 'No informado';
    }, [sourceData]);

    const clientCuit = useMemo(() => {
        return (sourceData?.clienteCuit || sourceData?.cuit || sourceData?.entidadCuit || '-').toString().trim();
    }, [sourceData]);

    // --- CORRECCIÓN: Resolvemos de forma síncrona en memoria la mejor opción ---
    const ganadora = useMemo(() => (ofertas.length > 0 ? ofertas[0] : null), [ofertas]);

    const masaSalarial = parseFloat(cotizacion?.masaSalarial || 0);
    const empleados = parseInt(cotizacion?.capitas || cotizacion?.cantEmpleados || 1, 10);
    const alicuotaActual = parseFloat(cotizacion?.alicuotaActual || 0); 
    const costoMensualActual = (masaSalarial * (alicuotaActual / 100));

    const stats = useMemo(() => {
        const oferta = ofertaParaCalculos;
        if (!oferta || parseFloat(oferta.alicuotaCotizada) <= 0) return { costoMensual: 0, costoPorCapita: 0, diferenciaMensual: 0, diferenciaAnual: 0, diferenciaPorcentual: 0 };
        const alicuota = parseFloat(oferta.alicuotaCotizada);
        const costoMensual = (masaSalarial * (alicuota / 100));
        const costoPorCapita = empleados > 0 ? costoMensual / empleados : 0;
        const diferenciaMensual = costoMensual - costoMensualActual;
        const diferenciaAnual = diferenciaMensual * 13;
        const diferenciaPorcentual = costoMensualActual > 0 ? ((costoMensual - costoMensualActual) / costoMensualActual) * 100 : 0;
        return { alicuota, costoMensual, costoPorCapita, diferenciaMensual, diferenciaAnual, diferenciaPorcentual };
    }, [ofertaParaCalculos, masaSalarial, empleados, costoMensualActual]);

    // --- ACCIÓN: EXPORTAR PRESUPUESTO OFICIAL PDF ---
    const handleDownloadPdf = async () => {
        setIsGenerating(true);
        try {
            const blob = await pdf(
                <PlantillaCotizacionArtPDF 
                    cotizacion={cotizacion} 
                    oferta={ofertaParaCalculos} 
                    stats={stats} 
                    fetchedClient={fetchedClient}
                    clientCuit={clientCuit}
                    formatCurrency={formatCurrency} 
                    formatPercent={formatPercent} 
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Cotizacion_ART_${fullName.replace(/\s+/g, '_')}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
            
            setNotification({ show: true, message: 'PDF oficial vectorial generado con éxito', type: 'success' });
        } catch (error) {
            console.error("Error exportando PDF vectorial:", error);
            setNotification({ show: true, message: 'Error al generar el presupuesto en PDF', type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    // --- ESCUDO CONTRA EL FLICKER SÍNCRONO ---
    if (loading || loadingAuth) return <div className="p-20 text-center font-bold text-slate-400 animate-pulse italic uppercase tracking-widest text-[10px] select-none">Sincronizando información de la cotización...</div>;

    return (
        <div className="animate-fade-in p-6 md:p-10 pb-20 text-left bg-white min-h-screen relative overflow-y-auto max-h-screen font-['Inter'] text-slate-900">
            <div className="max-w-[1600px] mx-auto space-y-6">
                
                {/* BANNER DE VINCULACIÓN EXITOSA */}
                {isContratoGenerado && (
                    <div className="bg-blue-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center justify-between animate-bounce-in border-b-4 border-blue-800 text-left">
                        <div className="flex items-center gap-4 text-left">
                            <div className="bg-white/20 p-2 rounded-lg"><Icons.History className="w-5 h-5 text-white" /></div>
                            <div className="text-left">
                                <p className="font-bold text-lg leading-none">Contrato Vinculado</p>
                                <p className="text-blue-100 text-sm mt-1 italic">N° de Contrato: <span className="font-mono bg-blue-700 px-2 py-0.5 rounded font-bold text-white tracking-wider">{cotizacion.nContratoVinculado || '---'}</span></p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm sticky top-0 z-10 gap-4 text-left">
                    <div className="flex items-center gap-4 text-left">
                        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500"><Icons.ArrowLeft /></button>
                        <div className="text-left">
                            <div className="flex items-center gap-2 text-left">
                                <h1 className="text-xl font-bold text-slate-800 uppercase italic">{fullName}</h1>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${isProspect ? 'bg-orange-50 text-orange-600 border-orange-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>{isProspect ? 'PROSPECTO' : 'CLIENTE'}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 text-left">
                                <div className="flex items-center gap-1"><Icons.User className="w-3.5 h-3.5 text-slate-400" /> {displayEmail}</div>
                                <div className="flex items-center gap-1 border-l border-slate-200 pl-3"><Icons.Lock className="w-3 h-3 text-slate-400" /> {displayPhone}</div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto h-[42px] text-left">
                        {/* --- IMPLEMENTACIÓN DE PERMISOS (MATRIZ ACL) --- */}
                        {isFullUser && (
                            <>
                                {isProspect && !isContratoGenerado ? (
                                    <button 
                                        onClick={() => setShowConfirmModal(true)}
                                        disabled={isGenerating}
                                        className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 font-medium text-sm shadow-md transition-all h-full whitespace-nowrap active:scale-95"
                                    >
                                        Convertir a Cliente
                                    </button>
                                ) : !isProspect && (
                                    <button 
                                        disabled={true}
                                        className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-slate-50 text-slate-500 px-4 py-2 rounded-lg border border-slate-200 font-medium text-sm h-full whitespace-nowrap shadow-inner"
                                    >
                                        Cliente Registrado
                                    </button>
                                )}

                                <div className="relative group flex-1 md:flex-none h-full text-left">
                                    <button 
                                        onClick={handleOpenContractModal} 
                                        disabled={isContratoGenerado || isProspect} 
                                        className={`w-full h-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm shadow-sm transition-all whitespace-nowrap ${
                                            (isContratoGenerado || isProspect) 
                                                ? 'bg-slate-50 text-slate-400 cursor-not-allowed border-slate-100 shadow-none' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                                        }`}
                                    >
                                        <Icons.Policy className="w-4 h-4" /> {isContratoGenerado ? 'Contrato Emitido' : 'Generar Contrato'}
                                    </button>
                                    {isProspect && !isContratoGenerado && (
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 bg-gray-800 text-white text-[10px] p-2 rounded shadow-xl text-center z-50 animate-fade-in font-bold uppercase tracking-tighter">
                                            Primero debe convertir el prospecto a cliente para habilitar esta función.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        
                        {/* Botón Descargar PDF siempre activo para todos */}
                        <button onClick={handleDownloadPdf} disabled={isGenerating} className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 font-medium text-sm shadow-md transition-all active:scale-95 h-full whitespace-nowrap">{isGenerating ? 'Generando...' : 'Descargar PDF'}</button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left">
                    <div className="space-y-6 text-left">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm text-left">
                            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2 text-left"><Icons.Company className="w-4 h-4 text-slate-400" /> Datos de Empresa</h3>
                            <div className="space-y-3 text-sm text-left">
                                <div className="flex justify-between border-b border-slate-50 pb-2 text-left"><span className="text-slate-500">Empleados</span><span className="font-semibold text-slate-800">{empleados}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2 text-left"><span className="text-slate-500">Masa Salarial</span><span className="font-semibold text-slate-800">{formatCurrency(masaSalarial)}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2 text-left"><span className="text-slate-500">CIIU</span><span className="font-semibold text-slate-800 font-mono font-bold text-blue-900">{cotizacion.ciiu || '-'}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2 text-left"><span className="text-slate-500">Alic. Actual (%)</span><span className="font-semibold text-slate-800">{formatPercent(alicuotaActual)}</span></div>
                                <div className="flex justify-between pt-2 text-left"><span className="text-slate-500">ART Actual</span><span className="font-semibold text-blue-600 uppercase">{cotizacion.artActual || 'No informada'}</span></div>
                                <div className="flex justify-between pt-2 text-left">
                                    <span className="text-slate-500">Afiliación</span>
                                    <span className={`font-black uppercase ${cotizacion.afiliacion ? 'text-emerald-600' : 'text-slate-400'}`}>
                                        {cotizacion.afiliacion ? 'SÍ' : 'NO'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {ofertaParaCalculos && (
                            <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-xl shadow-lg text-white text-left">
                                <h3 className="text-lg font-bold mb-6 italic text-left">Análisis de Ahorro</h3>
                                <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                                    <div className="bg-white/10 p-3 rounded-lg backdrop-blur-sm text-center">
                                        <p className="text-slate-300 text-[10px] uppercase font-bold mb-1 text-center">Costo Nuevo</p>
                                        <p className="text-lg font-bold text-emerald-400 text-center">{formatCurrency(stats.costoMensual)}</p>
                                    </div>
                                    <div className="bg-white/5 p-3 rounded-lg text-center">
                                        <p className="text-slate-400 text-[10px] uppercase font-bold mb-1 text-center">Costo Actual</p>
                                        <p className="text-lg font-bold text-slate-200 text-center">{formatCurrency(costoMensualActual)}</p>
                                    </div>
                                </div>
                                <div className="space-y-4 text-left">
                                    <div className="flex items-center justify-between text-left">
                                        <div className="flex items-center gap-3 text-left">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stats.diferenciaMensual < 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}><Icons.ChevronRight className="w-4 h-4 text-emerald-400" /></div>
                                            <span className="text-sm font-medium">Diferencia Mensual</span>
                                        </div>
                                        <span className={`font-bold ${stats.diferenciaMensual < 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(stats.diferenciaMensual)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-left">
                                        <div className="flex items-center gap-3 text-left">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stats.diferenciaAnual < 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}><Icons.History className="w-3.5 h-3.5 text-emerald-400" /></div>
                                            <span className="text-sm font-medium">Proyección Anual</span>
                                        </div>
                                        <span className={`font-bold ${stats.diferenciaAnual < 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(stats.diferenciaAnual)}</span>
                                    </div>
                                    <div className="pt-4 border-t border-white/10 flex justify-between items-center text-left">
                                        <span className="text-sm text-slate-400">Impacto Porcentual</span>
                                        <span className={`text-xl font-black ${stats.diferenciaPorcentual < 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPercent(stats.diferenciaPorcentual)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-2 space-y-4 text-left">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 italic text-left"><Icons.Trash className="w-5 h-5 text-yellow-500" /> Ranking de Ofertas</h3>
                        {listaVisual.map((oferta, index) => {
                             const isSelected = oferta === ofertaParaCalculos;
                             const estadoRow = oferta.estadoTarget || oferta.estadoCotizacion || oferta.estadoRow || 'Cotizada';
                             const isInvalid = ['Solicitada', 'Bloqueada', 'No cotiza', 'Pendiente', 'Stand By'].includes(estadoRow);
                             const alicuota = parseFloat(oferta.alicuotaCotizada) || 0;
                            return (
                                <div key={index} onClick={() => !isInvalid && alicuota > 0 && setSelectedComparacion(oferta)} className={`relative p-5 rounded-xl border transition-all group ${isSelected ? 'bg-blue-50 border-blue-200 shadow-md ring-1 ring-blue-200' : isInvalid ? 'bg-gray-50/50 border-gray-100 opacity-80 cursor-default' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md cursor-pointer text-left'}`}>
                                    {ganadora && oferta === ganadora && <div className="absolute -top-3 -right-3 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-3 py-1 rounded-full shadow-sm z-10">✓ MEJOR PRECIO</div>}
                                    <div className="flex justify-between items-center mb-4 text-left">
                                        <div className="flex items-center gap-3 text-left">
                                            <div className="text-left">
                                                <h4 className="font-bold text-slate-800 text-lg uppercase text-left">{oferta.compania}</h4>
                                                {isInvalid ? <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-gray-200 text-gray-500 uppercase mt-1">{estadoRow}</span> : <p className="text-xs text-slate-500 text-left">Oferta válida procesada</p>}
                                            </div>
                                        </div>
                                        {(!isInvalid && alicuota > 0) ? (
                                            <div className="text-right">
                                                <p className="text-3xl font-black text-slate-800">{oferta.alicuotaCotizada.toString().replace('.', ',')}%</p>
                                                <p className="text-xs text-slate-500">Alícuota final</p>
                                            </div>
                                        ) : (
                                            <div className="text-right">
                                                <p className="text-lg font-black text-slate-500 uppercase">{estadoRow}</p>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase text-right">Estado de Oferta</p>
                                            </div>
                                        )}
                                    </div>
                                    {!isInvalid && alicuota > 0 && (
                                        <div className="grid grid-cols-4 gap-4 border-t border-slate-200/60 pt-4 text-left">
                                            <div className="text-left"><p className="text-xs text-slate-500 mb-1 uppercase font-bold text-[10px]">Mensual</p><p className="font-semibold text-slate-700">{formatCurrency((masaSalarial * (alicuota/100)))}</p></div>
                                            <div className="text-left"><p className="text-xs text-slate-500 mb-1 uppercase font-bold text-[10px]">p/ Cápita</p><p className="font-semibold text-slate-700">{formatCurrency((masaSalarial * (alicuota/100))/empleados)}</p></div>
                                            <div className="text-left"><p className="text-xs text-slate-500 mb-1 uppercase font-bold text-[10px]">Anual</p><p className="font-semibold text-slate-700">{formatCurrency((masaSalarial * (alicuota/100)) * 13)}</p></div>
                                            <div className="text-right flex items-end justify-end"><button className={`text-sm font-medium ${isSelected ? 'text-blue-600 font-bold' : 'text-slate-400 group-hover:text-blue-500'}`}>{isSelected ? 'Seleccionada' : 'Seleccionar'}</button></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* --- ◄ DETECTOR PREMIUM: MODAL DE CONVERSIÓN CON FORMULARIOCLIENTE INTEGRADO --- */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in relative text-left">
                        
                        {/* Cabecera del Modal */}
                        <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 bg-orange-50/50 shrink-0">
                            <div className="text-left">
                                <h3 className="text-xl font-bold text-gray-800 leading-none">Completar Alta de Asegurado</h3>
                                <p className="text-xs text-orange-600 font-bold uppercase mt-1.5 tracking-tighter italic">
                                    Termine de cargar los datos de contacto y facturación para migrar el prospecto a cliente.
                                </p>
                            </div>
                            <button onClick={() => setShowConfirmModal(false)} className="text-gray-400 hover:text-gray-600 transition-all active:scale-95 text-xl">✕</button>
                        </div>

                        {/* Área del Formulario Pre-hidratado */}
                        <div className="flex-grow flex flex-col overflow-hidden min-h-0">
                            <div className="flex-grow overflow-hidden min-h-0">
                                <FormularioCliente 
                                    initialData={prospectInitialData} 
                                    onSaveSuccess={handleConversionSuccess}
                                    onCancel={() => setShowConfirmModal(false)} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ModalEdicionContrato 
                show={showContratoModal} 
                onClose={() => setShowContratoModal(false)} 
                clientData={clientDataForModal} 
                contrato={contratoPrefill} 
                onSuccess={handleContractSuccess} 
            />
            <ModalNotificacion show={notification.show} onClose={() => setNotification({ ...notification, show: false })} message={notification.message} type={notification.type} />
        </div>
    );
};

export default DetalleCotizacionArt;